import { LitElement, css, html, type PropertyValues } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import type { EditorView } from "@codemirror/view";
import type { Compartment } from "@codemirror/state";

@customElement("cpl-code-editor")
export class CplCodeEditor extends LitElement {
  @property({ type: String }) value = "";
  @property({ type: String }) language = "text";
  @property({ type: Boolean }) readOnly = false;
  @property({ type: Boolean }) disabled = false;
  @property({ type: String }) label = "Code";
  @property({ type: String, attribute: "min-height" }) minHeight = "12rem";
  private editor?: EditorView;
  private options?: Compartment;
  private engine?: typeof import("./classic-editor-engine");
  private mounting = false;
  @state() private loadError = "";
  private focusRequested = false;
  private applying = false;
  private pendingValue?: string;

  static styles = css`
    :host {
      display: block;
      min-width: 0;
      width: 100%;
      max-width: 100%;
      box-sizing: border-box;
    }
    .frame {
      border: 2px solid;
      border-color: #808080 #fff #fff #808080;
      box-shadow: inset 1px 1px #000;
      background: #fff;
      min-width: 0;
    }
    .frame:focus-within {
      outline: 1px dotted #000;
      outline-offset: 2px;
    }
    .frame,
    .cm-editor,
    .cm-scroller {
      width: 100%;
      min-width: 0;
      max-width: 100%;
      box-sizing: border-box;
    }
    .cm-editor {
      min-height: var(--editor-min-height, 12rem);
    }
    .cm-scroller {
      max-height: var(--editor-max-height, 32rem);
      overflow: auto;
    }
    ::-webkit-scrollbar {
      width: 16px;
      height: 16px;
    }
    ::-webkit-scrollbar-track {
      background: #dfdfdf;
    }
    ::-webkit-scrollbar-thumb {
      background: #c0c0c0;
      border: 2px solid;
      border-color: #fff #808080 #808080 #fff;
    }
    ::-webkit-scrollbar-corner {
      background: #c0c0c0;
    }
  `;

  private configuration() {
    const { editorLanguage, EditorState, EditorView } = this.engine!;
    return [
      editorLanguage(this.language),
      EditorState.readOnly.of(this.readOnly || this.disabled),
      EditorView.editable.of(!this.disabled),
      EditorView.contentAttributes.of({
        "aria-label": this.label,
        "aria-readonly": String(this.readOnly),
        "aria-disabled": String(this.disabled),
        tabindex: this.disabled ? "-1" : "0",
      }),
    ];
  }

  private async mount() {
    if (this.editor || this.mounting || this.loadError || !this.isConnected)
      return;
    this.mounting = true;
    try {
      this.engine = await import("./classic-editor-engine");
      if (!this.isConnected || this.editor) return;
      const {
        Compartment,
        EditorState,
        EditorView,
        keymap,
        lineNumbers,
        highlightActiveLine,
        defaultKeymap,
        history,
        historyKeymap,
        searchKeymap,
        highlightSelectionMatches,
        defaultHighlightStyle,
        syntaxHighlighting,
        indentOnInput,
      } = this.engine;
      this.options ??= new Compartment();
      const parent = this.renderRoot.querySelector<HTMLElement>(".frame");
      if (!parent) return;
      this.editor = new EditorView({
        parent,
        root: this.shadowRoot!,
        state: EditorState.create({
          doc: this.value,
          extensions: [
            lineNumbers(),
            history(),
            indentOnInput(),
            highlightActiveLine(),
            highlightSelectionMatches(),
            syntaxHighlighting(defaultHighlightStyle),
            keymap.of([...defaultKeymap, ...historyKeymap, ...searchKeymap]),
            this.options.of(this.configuration()),
            EditorView.theme({
              "&": { color: "#000", backgroundColor: "#fff", fontSize: "13px" },
              "&.cm-focused": { outline: "none" },
              ".cm-scroller": {
                fontFamily: 'Consolas, "Courier New", monospace',
                lineHeight: "1.5",
              },
              ".cm-gutters": {
                backgroundColor: "#dfdfdf",
                color: "#444",
                borderRight: "1px solid #808080",
              },
              ".cm-activeLine, .cm-activeLineGutter": {
                backgroundColor: "#f0f0f0",
              },
              ".cm-panels": { backgroundColor: "#c0c0c0", color: "#000" },
              ".cm-textfield, .cm-button": {
                borderRadius: "0",
                fontFamily: "Tahoma, Arial, sans-serif",
              },
              ".cm-button": {
                backgroundImage: "none",
                backgroundColor: "#c0c0c0",
              },
            }),
            EditorView.updateListener.of((update) => {
              if (update.docChanged && !this.applying) {
                this.value = update.state.doc.toString();
                this.dispatchEvent(
                  new CustomEvent("value-change", {
                    detail: { value: this.value },
                    bubbles: true,
                    composed: true,
                  }),
                );
              }
            }),
            EditorView.domEventHandlers({
              compositionend: () => {
                // Let the browser finish committing IME text before applying remote values.
                setTimeout(() => {
                  if (this.pendingValue !== undefined) {
                    const value = this.pendingValue;
                    this.pendingValue = undefined;
                    this.setDocument(value);
                    if (this.editor?.state.doc.toString() === value) {
                      // An IME input event may have updated the parent while the
                      // external value was queued. Publish the applied final value.
                      this.value = value;
                      this.dispatchEvent(
                        new CustomEvent("value-change", {
                          detail: { value },
                          bubbles: true,
                          composed: true,
                        }),
                      );
                    }
                  }
                }, 0);
              },
            }),
          ],
        }),
      });
      if (this.focusRequested && !this.disabled) {
        this.focusRequested = false;
        this.editor.focus();
      }
    } catch {
      this.loadError = "Editor could not load. Your text is preserved below.";
    } finally {
      this.mounting = false;
    }
  }

  private setDocument(value: string) {
    if (!this.editor || this.editor.state.doc.toString() === value) return;
    if (this.editor.composing) {
      this.pendingValue = value;
      return;
    }
    const previous = this.editor.state.doc.toString();
    let from = 0,
      oldEnd = previous.length,
      newEnd = value.length;
    while (from < oldEnd && from < newEnd && previous[from] === value[from])
      from++;
    while (
      oldEnd > from &&
      newEnd > from &&
      previous[oldEnd - 1] === value[newEnd - 1]
    ) {
      oldEnd--;
      newEnd--;
    }
    this.applying = true;
    try {
      this.editor.dispatch({
        changes: { from, to: oldEnd, insert: value.slice(from, newEnd) },
      });
      this.value = value;
    } finally {
      this.applying = false;
    }
  }

  protected updated(changed: PropertyValues) {
    this.mount();
    if (changed.has("value")) this.setDocument(this.value);
    if (
      ["language", "readOnly", "disabled", "label"].some((key) =>
        changed.has(key),
      )
    ) {
      this.editor?.dispatch({
        effects: this.options!.reconfigure(this.configuration()),
      });
    }
    this.style.setProperty("--editor-min-height", this.minHeight);
  }
  connectedCallback() {
    super.connectedCallback();
    if (this.hasUpdated) this.updateComplete.then(() => this.mount());
  }
  disconnectedCallback() {
    this.editor?.destroy();
    this.editor = undefined;
    this.pendingValue = undefined;
    super.disconnectedCallback();
  }
  focus(options?: FocusOptions) {
    if (this.editor && !this.disabled) this.editor.focus();
    else {
      this.focusRequested = !this.disabled;
      super.focus(options);
    }
  }
  render() {
    return html`<div class="frame"></div>
      ${
        this.loadError
          ? html`<p role="alert">${this.loadError}</p>
              <pre>${this.value}</pre>`
          : ""
      }`;
  }
}
